import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Form,
  Row,
  Col,
  message,
  Switch,
  Breadcrumb,
} from "antd";
import { Controller, useForm } from "react-hook-form";
import { createProductRequest } from "../../../api/requests/product";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { mutationOnErrorHandler } from "../../../utils/mutationUtils";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";

const createProductSchemaResolver = yupResolver(
  yup.object().shape({
    name: yup.string().required("Name is required"),
    description: yup.string(),
    customAttributes: yup.object().shape({
      type: yup.string(),
      size: yup
        .number()
        .positive("Size must be positive")
        .integer("Size must be an integer"),
      color: yup.string(),
    }),
    active: yup.boolean(),
  }),
);

function AddProductPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutateAsync: createProduct } = useMutation({
    mutationFn: async (data) => {
      const res = await createProductRequest(data);
      return res.data;
    },
    mutationKey: ["product/create"],
    onSuccess: (res) => {
      queryClient.invalidateQueries(["product"]);
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
      navigate(-1);
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: createProductSchemaResolver,
    defaultValues: {
      active: true,
    },
  });

  async function onSubmit(data) {
    console.log(data);
    const res = await createProduct(data);
    console.log("res----->", res);
  }

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-6">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
              <Breadcrumb
                items={[
                  {
                    title: <Link to={"/setup/products"}>Products</Link>,
                  },
                  {
                    title: <div className="text-lg">Create</div>,
                  },
                ]}
              />
              <Button
                type="primary"
                htmlType="submit"
                className="flex items-center justify-center"
              >
                Save
              </Button>
            </div>
            <div className="px-6 py-6">
              <Row gutter={18}>
                <Col span={12}>
                  <Form.Item
                    label="Name"
                    name="name"
                    validateStatus={errors.name ? "error" : ""}
                    help={errors.name && errors.name.message}
                  >
                    <Controller
                      control={control}
                      name="name"
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Name"
                          autoComplete="product-name"
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Description"
                    name="description"
                    validateStatus={errors.description ? "error" : ""}
                    help={errors.description && errors.description.message}
                  >
                    <Controller
                      control={control}
                      name="description"
                      render={({ field }) => (
                        <Input {...field} placeholder="Description" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Type"
                    name="customAttributes.type"
                    validateStatus={
                      errors.customAttributes?.type ? "error" : ""
                    }
                    help={errors.customAttributes?.type?.message}
                  >
                    <Controller
                      control={control}
                      name="customAttributes.type"
                      render={({ field }) => (
                        <Input {...field} placeholder="Type" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Size"
                    name="customAttributes.size"
                    validateStatus={
                      errors.customAttributes?.size ? "error" : ""
                    }
                    help={errors.customAttributes?.size?.message}
                  >
                    <Controller
                      control={control}
                      name="customAttributes.size"
                      render={({ field }) => (
                        <Input {...field} placeholder="Size" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Weight (lb)"
                    name="weight"
                    validateStatus={errors.weight ? "error" : ""}
                    help={errors?.weight?.message}
                  >
                    <Controller
                      control={control}
                      name="weight"
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Weight"
                          type="number"
                          min={0}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Color"
                    name="customAttributes.color"
                    validateStatus={
                      errors.customAttributes?.color ? "error" : ""
                    }
                    help={errors.customAttributes?.color?.message}
                  >
                    <Controller
                      control={control}
                      name="customAttributes.color"
                      render={({ field }) => (
                        <Input {...field} placeholder="Color" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Active"
                    name="active"
                    validateStatus={errors.active ? "error" : ""}
                    help={errors.active?.message}
                  >
                    <Controller
                      control={control}
                      name="active"
                      render={({ field }) => <Switch {...field} />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default AddProductPage;
